// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-06-20
// protected: NO  -> Halaman Laporan V2 (Reporting Pack P1-3 + P1-4).
// =============================================================================
// Laporan (read-only): tab Penjualan, Rekap Stock, Rekap MoU, Mutasi.
// Filter periode/store/lokasi (region/store ditegakkan backend via RLS + role guard).
// Export Excel per laporan. Tab tampil sesuai akses role.
import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, FileDown, BarChart3, ArrowLeftRight, Gift, Info } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { rupiah, downloadFile } from "@/v2/components/helpers";
import { TableScroll } from "@/v2/components/TableScroll";
import MutasiPage from "@/v2/pages/MutasiPage";

const LOC_LABEL = {
  GUDANG_SBY: "Gudang Surabaya", GUDANG_SMD: "Gudang Samarinda",
  STORE_AREMA: "Store Arema", STORE_BORNEO: "Store Borneo",
};
const REGION_LABEL = { SBY: "Surabaya", SMD: "Samarinda", GLOBAL: "Global" };
const REGION_OF = { GUDANG_SBY: "SBY", STORE_AREMA: "SBY", GUDANG_SMD: "SMD", STORE_BORNEO: "SMD" };
const WAREHOUSE_LABEL_BY_REGION = { SBY: "Gudang Surabaya", SMD: "Gudang Samarinda" };
// Label Status Transfer per baris rekap stok (mirror backend).
const transferLabel = (row) => {
  if (!row?.needs_transfer) return "Normal";
  const target = row.transfer_target_region;
  return `Perlu Transfer ke ${WAREHOUSE_LABEL_BY_REGION[target] || REGION_LABEL[target] || target}`;
};
const LOC_ACCESS = {
  OWNER: ["GUDANG_SBY", "GUDANG_SMD", "STORE_AREMA", "STORE_BORNEO"],
  KACAB_SBY: ["GUDANG_SBY", "STORE_AREMA"], KACAB_SMD: ["GUDANG_SMD", "STORE_BORNEO"],
  STAFF_STORE_AREMA: ["STORE_AREMA"], STAFF_STORE_BORNEO: ["STORE_BORNEO"],
  ADMIN_GUDANG_SBY: ["GUDANG_SBY"], ADMIN_GUDANG_SMD: ["GUDANG_SMD"],
};
const STORE_ACCESS = {
  OWNER: ["STORE_AREMA", "STORE_BORNEO"], KACAB_SBY: ["STORE_AREMA"], KACAB_SMD: ["STORE_BORNEO"],
  STAFF_STORE_AREMA: ["STORE_AREMA"], STAFF_STORE_BORNEO: ["STORE_BORNEO"],
};

const num = (n) => Number(n || 0).toLocaleString("id-ID");

// Pemilih lokasi/store; "ALL" hanya bila >1 opsi.
const ScopeSelect = ({ options, value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="mt-1" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      {options.length > 1 && <SelectItem value="ALL">Semua</SelectItem>}
      {options.map((o) => <SelectItem key={o} value={o}>{LOC_LABEL[o] || o}</SelectItem>)}
    </SelectContent>
  </Select>
);

// ---------------- Tab Penjualan ----------------
function SalesTab({ api, stores }) {
  const [scope, setScope] = useState(stores.length > 1 ? "ALL" : stores[0]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const qs = useCallback(() => {
    const q = new URLSearchParams();
    if (scope !== "ALL") q.set("store", scope);
    if (from) q.set("date_from", from);
    if (to) q.set("date_to", to);
    return q.toString();
  }, [scope, from, to]);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/api/v2/reports/sales?${qs()}`);
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal memuat"); setData(null); return; }
      setData(d);
    } finally { setLoading(false); }
  }, [api, qs]);
  useEffect(() => { run(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div><Label className="text-xs">Store</Label><ScopeSelect options={stores} value={scope} onChange={setScope} testid="v2-sales-store" /></div>
        <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" className="mt-1" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="v2-sales-from" /></div>
        <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" className="mt-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid="v2-sales-to" /></div>
        <div className="flex gap-2">
          <Button onClick={run} disabled={loading} data-testid="v2-sales-run">{loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Tampilkan</Button>
          <Button variant="outline" disabled={!data} data-testid="v2-sales-export" onClick={() => downloadFile(api, `/api/v2/reports/sales/export?${qs()}`, "laporan_penjualan.xlsx")}><FileDown className="w-4 h-4" /></Button>
        </div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4 rounded-2xl"><p className="text-xs text-muted-foreground">Penjualan Bersih</p><p className="text-xl font-semibold mt-1">{rupiah(data.totals.net)}</p></Card>
            <Card className="p-4 rounded-2xl"><p className="text-xs text-muted-foreground">Total Diskon</p><p className="text-xl font-semibold mt-1">{rupiah(data.totals.discount)}</p></Card>
            <Card className="p-4 rounded-2xl"><p className="text-xs text-muted-foreground">Qty Terjual</p><p className="text-xl font-semibold mt-1">{num(data.totals.qty)}</p></Card>
            <Card className="p-4 rounded-2xl"><p className="text-xs text-muted-foreground">Transaksi</p><p className="text-xl font-semibold mt-1">{num(data.totals.txn_count)}</p></Card>
          </div>
          <Card className="p-5 rounded-2xl">
            <TableScroll>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Merchandize</TableHead><TableHead>Club</TableHead><TableHead>Size</TableHead>
                <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Kotor</TableHead>
                <TableHead className="text-right">Diskon</TableHead><TableHead className="text-right">Bersih</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6 text-sm">Tidak ada penjualan pada periode ini.</TableCell></TableRow>}
                {data.rows.map((r, i) => (
                  <TableRow key={i} data-testid="v2-sales-row">
                    <TableCell className="text-sm">{r.product_name}</TableCell><TableCell>{r.club}</TableCell><TableCell>{r.size}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(r.qty)}</TableCell>
                    <TableCell className="text-right tabular-nums">{rupiah(r.gross)}</TableCell>
                    <TableCell className="text-right tabular-nums">{rupiah(r.discount)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{rupiah(r.net)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableScroll>
          </Card>
        </>
      )}
    </div>
  );
}

// ---------------- Tab Rekap Stock ----------------
// Badge kategori barang — mirror label QR fisik (label_thermal.html): JUAL hijau, MoU amber.
const CategoryBadge = ({ cat }) => {
  if (cat === "MoU") {
    return <Badge variant="warning" className="font-medium" data-testid="v2-stock-cat-badge">MoU</Badge>;
  }
  if (cat === "Jual") {
    return <Badge variant="success" className="font-medium" data-testid="v2-stock-cat-badge">JUAL</Badge>;
  }
  return <span className="text-muted-foreground">-</span>;
};

function StockTab({ api, locs }) {
  const [scope, setScope] = useState(locs.length > 1 ? "ALL" : locs[0]);
  const [asOf, setAsOf] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("all"); // all | transfer
  const [cat, setCat] = useState("all"); // all | Jual | MoU

  const qs = useCallback(() => {
    const q = new URLSearchParams();
    if (scope !== "ALL") q.set("location", scope);
    if (asOf) q.set("as_of", asOf);
    return q.toString();
  }, [scope, asOf]);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/api/v2/reports/stock-recap?${qs()}`);
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal memuat"); setData(null); return; }
      setData(d);
    } finally { setLoading(false); }
  }, [api, qs]);
  useEffect(() => { run(); }, []); // eslint-disable-line

  const allRows = data?.rows || [];
  const transferCount = allRows.filter((r) => r.needs_transfer).length;
  let rows = view === "transfer" ? allRows.filter((r) => r.needs_transfer) : allRows;
  if (cat !== "all") rows = rows.filter((r) => r.item_category === cat);
  const visibleTotal = rows.reduce((s, r) => s + (r.saldo || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div><Label className="text-xs">Lokasi</Label><ScopeSelect options={locs} value={scope} onChange={setScope} testid="v2-stock-loc" /></div>
        <div><Label className="text-xs">Per Tanggal (opsional)</Label><Input type="date" className="mt-1" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="v2-stock-asof" /></div>
        <div>
          <Label className="text-xs">Kategori</Label>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="mt-1" data-testid="v2-stock-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="Jual">JUAL</SelectItem>
              <SelectItem value="MoU">MoU</SelectItem>
              <SelectItem value="CACAT">CACAT</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button onClick={run} disabled={loading} data-testid="v2-stock-run">{loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Tampilkan</Button>
          <Button variant="outline" disabled={!data} data-testid="v2-stock-export" onClick={() => downloadFile(api, `/api/v2/reports/stock-recap/export?${qs()}`, "rekap_stock.xlsx")}><FileDown className="w-4 h-4" /></Button>
        </div>
      </div>
      {data && (
        <Card className="p-5 rounded-2xl">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="text-sm">Total stok{cat !== "all" || view === "transfer" ? " (tampil)" : ""}: <b className="tabular-nums" data-testid="v2-stock-total">{num(visibleTotal)}</b> unit</p>
            <Tabs value={view} onValueChange={setView}>
              <TabsList data-testid="v2-stock-view-tabs">
                <TabsTrigger value="all" data-testid="v2-stock-view-all">Semua</TabsTrigger>
                <TabsTrigger value="transfer" data-testid="v2-stock-view-transfer">
                  <ArrowLeftRight className="w-3.5 h-3.5 mr-1" />Perlu Transfer
                  {transferCount > 0 && <span className="ml-1.5 rounded-full bg-[hsl(var(--badge-warning))]/15 text-[hsl(var(--badge-warning))] px-1.5 text-xs tabular-nums">{transferCount}</span>}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {/* UAT-FIX-5 Slice 4: keterangan agar tak dikira data hilang. */}
          <p className="text-[11px] text-muted-foreground mb-3 flex items-start gap-1.5" data-testid="v2-stock-mou-note">
            <Info className="w-3.5 h-3.5 mt-px shrink-0" />
            <span>Stok <b>MoU di store</b> tidak ditampilkan (sudah diserahkan ke klub) — lihat tab <b>Rekap MoU</b>. MoU di gudang tetap dihitung sebagai stok.</span>
          </p>
          {(data.defect_units || 0) > 0 && (
            <p className="text-[11px] text-muted-foreground mb-3 flex items-start gap-1.5" data-testid="v2-stock-cacat-note">
              <Info className="w-3.5 h-3.5 mt-px shrink-0" />
              <span>Kelompok <b>CACAT</b> ({num(data.defect_units)} unit) = barang cacat menunggu/proses retur — <b>bukan</b> stok jual maupun MoU.</span>
            </p>
          )}
          <TableScroll>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Merchandize</TableHead><TableHead>Club</TableHead><TableHead>Size</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
              <TableHead>Lokasi Saat Ini</TableHead><TableHead>Region Produksi</TableHead>
              <TableHead>Region Pemilik</TableHead><TableHead>Status Transfer</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6 text-sm">{view === "transfer" ? "Tidak ada Merchandize yang perlu transfer." : "Tidak ada stok."}</TableCell></TableRow>}
              {rows.map((r, i) => (
                <TableRow key={i} data-testid="v2-stock-row">
                  <TableCell className="text-sm">{r.product_name}</TableCell><TableCell>{r.club}</TableCell><TableCell>{r.size}</TableCell>
                  <TableCell data-testid="v2-stock-category"><CategoryBadge cat={r.item_category} /></TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{num(r.saldo)}</TableCell>
                  <TableCell>{LOC_LABEL[r.location] || r.location}</TableCell>
                  <TableCell>{REGION_LABEL[r.production_region] || r.production_region || "-"}</TableCell>
                  <TableCell>{REGION_LABEL[r.owner_region] || r.owner_region || "-"}</TableCell>
                  <TableCell data-testid="v2-stock-transfer-status">
                    {r.needs_transfer
                      ? <Badge variant="warning" className="font-medium whitespace-nowrap">{transferLabel(r)}</Badge>
                      : <Badge variant="success">Normal</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableScroll>
        </Card>
      )}
    </div>
  );
}

// ---------------- Tab Rekap MoU ----------------
function MouTab({ api, stores }) {
  const [scope, setScope] = useState(stores.length > 1 ? "ALL" : stores[0]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const qs = useCallback(() => {
    const q = new URLSearchParams();
    if (scope !== "ALL") q.set("store", scope);
    if (from) q.set("date_from", from);
    if (to) q.set("date_to", to);
    return q.toString();
  }, [scope, from, to]);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/api/v2/reports/mou-recap?${qs()}`);
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal memuat"); setData(null); return; }
      setData(d);
    } finally { setLoading(false); }
  }, [api, qs]);
  useEffect(() => { run(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div><Label className="text-xs">Store</Label><ScopeSelect options={stores} value={scope} onChange={setScope} testid="v2-mou-store" /></div>
        <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" className="mt-1" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="v2-mou-from" /></div>
        <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" className="mt-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid="v2-mou-to" /></div>
        <div className="flex gap-2">
          <Button onClick={run} disabled={loading} data-testid="v2-mou-run">{loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Tampilkan</Button>
          <Button variant="outline" disabled={!data} data-testid="v2-mou-export" onClick={() => downloadFile(api, `/api/v2/reports/mou-recap/export?${qs()}`, "rekap_mou.xlsx")}><FileDown className="w-4 h-4" /></Button>
        </div>
      </div>
      {data && (
        <Card className="p-5 rounded-2xl">
          <p className="text-sm mb-2">Total penerimaan MoU: <b className="tabular-nums">{num(data.total)}</b> unit</p>
          <TableScroll>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Store</TableHead><TableHead>Club</TableHead><TableHead>Merchandize</TableHead><TableHead>Size</TableHead><TableHead className="text-right">Qty</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6 text-sm">Tidak ada penerimaan MoU.</TableCell></TableRow>}
              {data.rows.map((r, i) => (
                <TableRow key={i} data-testid="v2-mou-row">
                  <TableCell>{LOC_LABEL[r.store] || r.store}</TableCell><TableCell>{r.club}</TableCell>
                  <TableCell className="text-sm">{r.product_name}</TableCell><TableCell>{r.size}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{num(r.qty)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableScroll>
        </Card>
      )}
    </div>
  );
}
// ---------------- Tab Rekap Compliment ----------------
// Badge jalur compliment: Kasir (Store) vs Gudang.
const JalurBadge = ({ jalurKey }) => (
  jalurKey === "COMPLIMENT_GUDANG"
    ? <Badge variant="secondary" className="font-medium whitespace-nowrap" data-testid="v2-cmp-jalur">Gudang</Badge>
    : <Badge variant="outline" className="font-medium whitespace-nowrap" data-testid="v2-cmp-jalur">Kasir (Store)</Badge>
);

function ComplimentTab({ api, locs }) {
  const [scope, setScope] = useState(locs.length > 1 ? "ALL" : locs[0]);
  const regions = Array.from(new Set(locs.map((l) => REGION_OF[l]).filter(Boolean)));
  const [region, setRegion] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jalur, setJalur] = useState("all"); // all | kasir | gudang
  const [cat, setCat] = useState("all"); // all | Jual | MoU
  const [club, setClub] = useState("all");

  const serverQs = useCallback(() => {
    const q = new URLSearchParams();
    if (scope !== "ALL") q.set("location", scope);
    if (region !== "all") q.set("region", region);
    if (from) q.set("date_from", from);
    if (to) q.set("date_to", to);
    return q;
  }, [scope, region, from, to]);

  const exportQs = useCallback(() => {
    const q = serverQs();
    if (jalur !== "all") q.set("jalur", jalur);
    if (club !== "all") q.set("club", club);
    if (cat !== "all") q.set("category", cat === "Jual" ? "JUAL" : "MoU");
    return q.toString();
  }, [serverQs, jalur, club, cat]);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/api/v2/reports/compliment-recap?${serverQs().toString()}`);
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal memuat"); setData(null); return; }
      setData(d);
    } finally { setLoading(false); }
  }, [api, serverQs]);
  useEffect(() => { run(); }, []); // eslint-disable-line

  const allRows = data?.rows || [];
  const clubOptions = Array.from(new Set(allRows.map((r) => r.club).filter(Boolean))).sort();
  const jalurKeyOf = { kasir: "COMPLIMENT", gudang: "COMPLIMENT_GUDANG" };
  let rows = allRows;
  if (jalur !== "all") rows = rows.filter((r) => r.jalur_key === jalurKeyOf[jalur]);
  if (cat !== "all") rows = rows.filter((r) => r.item_category === cat);
  if (club !== "all") rows = rows.filter((r) => r.club === club);
  const visibleTotal = rows.reduce((s, r) => s + (r.qty || 0), 0);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[hsl(var(--badge-warning))]/40 bg-[hsl(var(--badge-warning))]/10 px-3 py-2 text-xs text-[hsl(var(--badge-warning))]" data-testid="v2-cmp-note">
        Compliment <b>tidak punya nilai uang</b>, tidak masuk penjualan/penagihan. Rekap ini mencatat <b>jumlah unit</b> &amp; <b>penerima</b> saja.
      </div>
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div><Label className="text-xs">Lokasi</Label><ScopeSelect options={locs} value={scope} onChange={setScope} testid="v2-cmp-loc" /></div>
        {regions.length > 1 && (
          <div>
            <Label className="text-xs">Region</Label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="mt-1" data-testid="v2-cmp-region"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="SBY">Surabaya</SelectItem>
                <SelectItem value="SMD">Samarinda</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" className="mt-1" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="v2-cmp-from" /></div>
        <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" className="mt-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid="v2-cmp-to" /></div>
      </div>
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="text-xs">Jalur</Label>
          <Select value={jalur} onValueChange={setJalur}>
            <SelectTrigger className="mt-1" data-testid="v2-cmp-jalur-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="kasir">Kasir (Store)</SelectItem>
              <SelectItem value="gudang">Gudang</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Kategori</Label>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="mt-1" data-testid="v2-cmp-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="Jual">JUAL</SelectItem>
              <SelectItem value="MoU">MoU</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Klub</Label>
          <Select value={club} onValueChange={setClub}>
            <SelectTrigger className="mt-1" data-testid="v2-cmp-club-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              {clubOptions.map((cl) => <SelectItem key={cl} value={cl}>{cl}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button onClick={run} disabled={loading} data-testid="v2-cmp-run">{loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Tampilkan</Button>
          <Button variant="outline" disabled={!data} data-testid="v2-cmp-export" onClick={() => downloadFile(api, `/api/v2/reports/compliment-recap/export?${exportQs()}`, "rekap_compliment.xlsx")}><FileDown className="w-4 h-4" /></Button>
        </div>
      </div>
      {data && (
        <Card className="p-5 rounded-2xl">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <p className="text-sm">Total unit: <b className="tabular-nums" data-testid="v2-cmp-total">{num(visibleTotal)}</b></p>
            {Object.entries(data.by_jalur || {}).map(([k, v]) => (
              <span key={k} className="text-xs rounded-full bg-muted px-2 py-0.5 tabular-nums">{k}: {num(v)}</span>
            ))}
          </div>
          <TableScroll>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Jalur</TableHead><TableHead>Lokasi</TableHead><TableHead>Penerima</TableHead>
              <TableHead>Merchandize</TableHead><TableHead>Club</TableHead><TableHead>Size</TableHead>
              <TableHead>Kategori</TableHead><TableHead className="text-right">Qty</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6 text-sm">Tidak ada compliment pada periode/filter ini.</TableCell></TableRow>}
              {rows.map((r, i) => (
                <TableRow key={i} data-testid="v2-cmp-row">
                  <TableCell><JalurBadge jalurKey={r.jalur_key} /></TableCell>
                  <TableCell>{LOC_LABEL[r.location] || r.location}</TableCell>
                  <TableCell className="text-sm font-medium" data-testid="v2-cmp-recipient">{r.recipient || "-"}</TableCell>
                  <TableCell className="text-sm">{r.product_name}</TableCell><TableCell>{r.club}</TableCell><TableCell>{r.size}</TableCell>
                  <TableCell data-testid="v2-cmp-category"><CategoryBadge cat={r.item_category} /></TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{num(r.qty)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableScroll>
        </Card>
      )}
    </div>
  );
}


// ---------------- Tab Rekap Retur ----------------
const RETUR_BADGE = {
  DRAFT: "border-[hsl(var(--badge-neutral))]/40 bg-[hsl(var(--badge-neutral))]/10 text-[hsl(var(--badge-neutral))]",
  SHIPPED: "border-[hsl(var(--badge-info))]/40 bg-[hsl(var(--badge-info))]/10 text-[hsl(var(--badge-info))]",
  RECEIVED: "border-[hsl(var(--badge-success))]/40 bg-[hsl(var(--badge-success))]/10 text-[hsl(var(--badge-success))]",
  CANCELLED: "border-[hsl(var(--badge-danger))]/40 bg-[hsl(var(--badge-danger))]/10 text-[hsl(var(--badge-danger))]",
  CLOSED: "border-[hsl(var(--badge-success))]/40 bg-[hsl(var(--badge-success))]/10 text-[hsl(var(--badge-success))]",
};
const ReturStatusBadge = ({ status, label }) => (
  <Badge variant="outline" className={`${RETUR_BADGE[status] || RETUR_BADGE.DRAFT} font-medium whitespace-nowrap`} data-testid="v2-ret-status">{label || status}</Badge>
);

function ReturTab({ api, stores }) {
  const [scope, setScope] = useState(stores.length > 1 ? "ALL" : stores[0]);
  const regions = Array.from(new Set(stores.map((l) => REGION_OF[l]).filter(Boolean)));
  const [region, setRegion] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("all");
  const [club, setClub] = useState("all");

  const serverQs = useCallback(() => {
    const q = new URLSearchParams();
    if (scope !== "ALL") q.set("store", scope);
    if (region !== "all") q.set("region", region);
    if (from) q.set("date_from", from);
    if (to) q.set("date_to", to);
    return q;
  }, [scope, region, from, to]);

  const exportQs = useCallback(() => {
    const q = serverQs();
    if (status !== "all") q.set("status", status);
    if (club !== "all") q.set("club", club);
    return q.toString();
  }, [serverQs, status, club]);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/api/v2/reports/return-recap?${serverQs().toString()}`);
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal memuat"); setData(null); return; }
      setData(d);
    } finally { setLoading(false); }
  }, [api, serverQs]);
  useEffect(() => { run(); }, []); // eslint-disable-line

  const allRows = data?.rows || [];
  const clubOptions = Array.from(new Set(allRows.map((r) => r.club).filter(Boolean))).sort();
  const statusOptions = Array.from(new Set(allRows.map((r) => r.status).filter(Boolean)));
  let rows = allRows;
  if (status !== "all") rows = rows.filter((r) => r.status === status);
  if (club !== "all") rows = rows.filter((r) => r.club === club);
  const visibleTotal = rows.reduce((s, r) => s + (r.qty || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div><Label className="text-xs">Store</Label><ScopeSelect options={stores} value={scope} onChange={setScope} testid="v2-ret-store" /></div>
        {regions.length > 1 && (
          <div>
            <Label className="text-xs">Region</Label>
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="mt-1" data-testid="v2-ret-region"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="SBY">Surabaya</SelectItem>
                <SelectItem value="SMD">Samarinda</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" className="mt-1" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="v2-ret-from" /></div>
        <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" className="mt-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid="v2-ret-to" /></div>
      </div>
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="mt-1" data-testid="v2-ret-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              {statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Klub</Label>
          <Select value={club} onValueChange={setClub}>
            <SelectTrigger className="mt-1" data-testid="v2-ret-club-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              {clubOptions.map((cl) => <SelectItem key={cl} value={cl}>{cl}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 sm:col-span-2">
          <Button onClick={run} disabled={loading} data-testid="v2-ret-run">{loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Tampilkan</Button>
          <Button variant="outline" disabled={!data} data-testid="v2-ret-export" onClick={() => downloadFile(api, `/api/v2/reports/return-recap/export?${exportQs()}`, "rekap_retur.xlsx")}><FileDown className="w-4 h-4" /></Button>
        </div>
      </div>
      {data && (
        <Card className="p-5 rounded-2xl">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <p className="text-sm">Total unit retur: <b className="tabular-nums" data-testid="v2-ret-total">{num(visibleTotal)}</b></p>
            {Object.entries(data.by_status || {}).map(([k, v]) => (
              <span key={k} className="text-xs rounded-full bg-muted px-2 py-0.5 tabular-nums">{k}: {num(v)}</span>
            ))}
          </div>
          <TableScroll>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Store</TableHead><TableHead>Status</TableHead><TableHead>Alasan</TableHead>
              <TableHead>Merchandize</TableHead><TableHead>Club</TableHead><TableHead>Size</TableHead>
              <TableHead>Kategori</TableHead><TableHead className="text-right">Qty</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6 text-sm">Tidak ada retur pada periode/filter ini.</TableCell></TableRow>}
              {rows.map((r, i) => (
                <TableRow key={i} data-testid="v2-ret-row">
                  <TableCell>{LOC_LABEL[r.store] || r.store}</TableCell>
                  <TableCell><ReturStatusBadge status={r.status} label={r.status_label} /></TableCell>
                  <TableCell className="text-sm max-w-[220px] truncate" title={r.reason}>{r.reason || "-"}</TableCell>
                  <TableCell className="text-sm">{r.product_name}</TableCell><TableCell>{r.club}</TableCell><TableCell>{r.size}</TableCell>
                  <TableCell data-testid="v2-ret-category"><CategoryBadge cat={r.item_category} /></TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{num(r.qty)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableScroll>
        </Card>
      )}
    </div>
  );
}

// ---------------- Tab Rekap Pajak (PPN) — Slice 3 ----------------
function TaxTab({ api, stores }) {
  const [scope, setScope] = useState(stores.length > 1 ? "ALL" : stores[0]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const qs = useCallback(() => {
    const q = new URLSearchParams();
    if (scope !== "ALL") q.set("store", scope);
    if (from) q.set("date_from", from);
    if (to) q.set("date_to", to);
    return q.toString();
  }, [scope, from, to]);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/api/v2/reports/tax-recap?${qs()}`);
      const d = await r.json();
      if (!r.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Gagal memuat"); setData(null); return; }
      setData(d);
    } finally { setLoading(false); }
  }, [api, qs]);
  useEffect(() => { run(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[hsl(var(--badge-info))]/40 bg-[hsl(var(--badge-info))]/10 px-3 py-2 text-xs text-[hsl(var(--badge-info))]" data-testid="v2-tax-note">
        Rekap <b>PPN yang dipungut store</b> (urusan store↔customer). <b>Tidak termasuk</b> penagihan ETAMS ke klub.
        Hanya transaksi <b>Jual</b> yang dikenai pajak; Compliment/MoU tidak.
      </div>
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div><Label className="text-xs">Store</Label><ScopeSelect options={stores} value={scope} onChange={setScope} testid="v2-tax-store" /></div>
        <div><Label className="text-xs">Dari Tanggal</Label><Input type="date" className="mt-1" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="v2-tax-from" /></div>
        <div><Label className="text-xs">Sampai Tanggal</Label><Input type="date" className="mt-1" value={to} onChange={(e) => setTo(e.target.value)} data-testid="v2-tax-to" /></div>
        <div className="flex gap-2">
          <Button onClick={run} disabled={loading} data-testid="v2-tax-run">{loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Tampilkan</Button>
          <Button variant="outline" disabled={!data} data-testid="v2-tax-export" onClick={() => downloadFile(api, `/api/v2/reports/tax-recap/export?${qs()}`, "rekap_ppn.xlsx")}><FileDown className="w-4 h-4" /></Button>
        </div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Card className="p-4 rounded-2xl"><p className="text-xs text-muted-foreground">Transaksi Kena Pajak</p><p className="text-xl font-semibold mt-1" data-testid="v2-tax-count">{num(data.count)}</p></Card>
            <Card className="p-4 rounded-2xl"><p className="text-xs text-muted-foreground">Total DPP</p><p className="text-xl font-semibold mt-1" data-testid="v2-tax-total-dpp">{rupiah(data.total_dpp)}</p></Card>
            <Card className="p-4 rounded-2xl"><p className="text-xs text-muted-foreground">Total PPN Terkumpul</p><p className="text-xl font-semibold mt-1" data-testid="v2-tax-total-tax">{rupiah(data.total_tax)}</p></Card>
          </div>
          <Card className="p-5 rounded-2xl">
            <TableScroll>
            <Table>
              <TableHeader><TableRow>
                <TableHead>No Invoice</TableHead><TableHead>Waktu</TableHead><TableHead>Store</TableHead>
                <TableHead className="text-right">DPP</TableHead><TableHead className="text-right">PPN %</TableHead>
                <TableHead className="text-right">PPN</TableHead><TableHead className="text-right">Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6 text-sm">Tidak ada transaksi kena pajak pada periode ini.</TableCell></TableRow>}
                {data.rows.map((r, i) => (
                  <TableRow key={i} data-testid="v2-tax-row">
                    <TableCell className="font-mono text-xs">{r.invoice_no}</TableCell>
                    <TableCell className="text-sm">{r.waktu}</TableCell>
                    <TableCell>{LOC_LABEL[r.store] || r.store}</TableCell>
                    <TableCell className="text-right tabular-nums">{rupiah(r.dpp)}</TableCell>
                    <TableCell className="text-right tabular-nums">{Number(r.tax_percent)}%</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{rupiah(r.tax_amount)}</TableCell>
                    <TableCell className="text-right tabular-nums">{rupiah(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableScroll>
          </Card>
        </>
      )}
    </div>
  );
}



export default function LaporanPage() {
  const { me, api } = useV2Auth();
  const role = me?.role;
  const stores = STORE_ACCESS[role] || [];
  const locs = LOC_ACCESS[role] || [];
  const hasStores = stores.length > 0;
  const hasLocs = locs.length > 0;
  const [tab, setTab] = useState(hasStores ? "penjualan" : "stock");

  // SLICE S2 deep-link: buka tab via ?tab=... (mis. dari kartu Dashboard "Compliment Store").
  // Tanpa parameter -> perilaku persis seperti sebelumnya. Nilai tak dikenal -> jatuh ke default (tanpa error).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (!urlTab) return;
    const valid = new Set([
      ...(hasStores ? ["penjualan", "mou", "retur", "pajak"] : []),
      ...(hasLocs ? ["stock", "compliment", "mutasi"] : []),
    ]);
    if (valid.has(urlTab)) setTab(urlTab);
  }, [searchParams, hasStores, hasLocs]);


  return (
    <div className="space-y-5" data-testid="v2-laporan-page">
      <h2 className="text-2xl font-semibold flex items-center gap-2"><BarChart3 className="w-6 h-6 text-primary" />Laporan</h2>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="v2-laporan-tabs">
          {hasStores && <TabsTrigger value="penjualan" data-testid="v2-tab-penjualan">Penjualan</TabsTrigger>}
          {hasLocs && <TabsTrigger value="stock" data-testid="v2-tab-stock">Rekap Stock</TabsTrigger>}
          {hasStores && <TabsTrigger value="mou" data-testid="v2-tab-mou">Rekap MoU</TabsTrigger>}
          {hasLocs && <TabsTrigger value="compliment" data-testid="v2-tab-compliment"><Gift className="w-3.5 h-3.5 mr-1" />Compliment</TabsTrigger>}
          {hasStores && <TabsTrigger value="retur" data-testid="v2-tab-retur">Retur</TabsTrigger>}
          {hasStores && <TabsTrigger value="pajak" data-testid="v2-tab-pajak">Pajak (PPN)</TabsTrigger>}
          {hasLocs && <TabsTrigger value="mutasi" data-testid="v2-tab-mutasi">Mutasi</TabsTrigger>}
        </TabsList>
        {hasStores && <TabsContent value="penjualan"><Card className="p-5 rounded-2xl"><SalesTab api={api} stores={stores} /></Card></TabsContent>}
        {hasLocs && <TabsContent value="stock"><Card className="p-5 rounded-2xl"><StockTab api={api} locs={locs} /></Card></TabsContent>}
        {hasStores && <TabsContent value="mou"><Card className="p-5 rounded-2xl"><MouTab api={api} stores={stores} /></Card></TabsContent>}
        {hasLocs && <TabsContent value="compliment"><Card className="p-5 rounded-2xl"><ComplimentTab api={api} locs={locs} /></Card></TabsContent>}
        {hasStores && <TabsContent value="retur"><Card className="p-5 rounded-2xl"><ReturTab api={api} stores={stores} /></Card></TabsContent>}
        {hasStores && <TabsContent value="pajak"><Card className="p-5 rounded-2xl"><TaxTab api={api} stores={stores} /></Card></TabsContent>}
        {hasLocs && <TabsContent value="mutasi"><MutasiPage /></TabsContent>}
      </Tabs>
    </div>
  );
}
